import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import PasswordInput from '../components/PasswordInput';

type JobTitle = 'CTO' | 'Product Owner' | 'Project Sponsor' | 'Other';

interface StakeholderInfo {
  name: string;
  email: string;
  projectId: string;
  tenantId: string;
}

export default function ClientOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1 fields
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState<JobTitle>('Product Owner');
  const [password, setPassword] = useState('');

  // Step 2 — project info (loaded from API)
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [orgName, setOrgName] = useState('');

  // Step 3 — notifications
  const [notifyChangeOrders] = useState(true);
  const [notifyMilestones, setNotifyMilestones] = useState(true);
  const [notifyWeekly, setNotifyWeekly] = useState(false);
  const [notifyDeployments, setNotifyDeployments] = useState(false);
  const [channel, setChannel] = useState<'EMAIL' | 'IN_APP'>('EMAIL');

  const stakeholderInfo: StakeholderInfo = {
    name: searchParams.get('name') ?? '',
    email: searchParams.get('email') ?? '',
    projectId: searchParams.get('projectId') ?? '',
    tenantId: searchParams.get('tenantId') ?? '',
  };

  useEffect(() => {
    if (stakeholderInfo.name) setName(stakeholderInfo.name);
  }, [stakeholderInfo.name]);

  useEffect(() => {
    if (stakeholderInfo.projectId) {
      apiFetch<{ name: string; domain?: string; description?: string }>(`/projects/${stakeholderInfo.projectId}`)
        .then(project => {
          setProjectName(project.name);
          setProjectDescription(project.description ?? 'A software project managed through BluePrint SDLC.');
        })
        .catch(() => setProjectName('Your Project'));

      apiFetch<{ name: string }>(`/tenants/${stakeholderInfo.tenantId}`)
        .then(tenant => setOrgName(tenant.name))
        .catch(() => setOrgName('Your delivery team'));
    }
  }, [stakeholderInfo.projectId, stakeholderInfo.tenantId]);

  const handleComplete = async () => {
    setLoading(true);
    try {
      if (stakeholderInfo.projectId) {
        await apiFetch(`/projects/${stakeholderInfo.projectId}/stakeholders/${searchParams.get('stakeholderId')}`, {
          method: 'PATCH',
          body: JSON.stringify({
            notifyOnChangeOrders: notifyChangeOrders,
            notifyOnMilestones: notifyMilestones,
            notifyOnWeeklySummary: notifyWeekly,
            notifyOnDeployments: notifyDeployments,
            notificationChannel: channel,
          }),
        });
      }
      navigate('/client/dashboard');
    } catch {
      navigate('/client/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 560, margin: '40px auto', padding: 40,
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 16,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 14,
    border: '1px solid var(--border)', background: 'var(--surface-alt)',
    color: 'var(--text)', boxSizing: 'border-box',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 28px', borderRadius: 8, border: 'none', cursor: 'pointer',
    background: 'var(--primary)', color: '#fff',
    fontWeight: 600, fontSize: 15,
  };

  const stepIndicator = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 32 }}>
      {[1, 2, 3].map(s => (
        <div
          key={s}
          style={{
            width: 10, height: 10, borderRadius: '50%',
            background: s === step ? 'var(--primary)' : 'var(--border)',
            transition: 'background 0.2s',
          }}
        />
      ))}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', padding: '20px' }}>
      <div style={cardStyle}>
        {stepIndicator}

        {step === 1 && (
          <>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>Welcome to BluePrint</h2>
            <p style={{ margin: '0 0 24px', color: 'var(--text-secondary)', fontSize: 14 }}>
              {"Let's get your account set up."}
            </p>

            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>Full name</span>
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
            </label>

            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>Job title</span>
              <select
                value={jobTitle}
                onChange={e => setJobTitle(e.target.value as JobTitle)}
                style={inputStyle}
              >
                <option>CTO</option>
                <option>Product Owner</option>
                <option>Project Sponsor</option>
                <option>Other</option>
              </select>
            </label>

            <label style={{ display: 'block', marginBottom: 24 }}>
              <span style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                Password <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional — skip to use magic links)</span>
              </span>
              <PasswordInput value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
            </label>

            <button onClick={() => setStep(2)} style={buttonStyle}>Continue →</button>
          </>
        )}

        {step === 2 && (
          <>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>Your Project</h2>
            <p style={{ margin: '0 0 24px', color: 'var(--text-secondary)', fontSize: 14 }}>
              {"Here's what's being built for you."}
            </p>

            <div style={{
              padding: 24, borderRadius: 12,
              background: 'var(--surface-alt)', border: '1px solid var(--border)',
              marginBottom: 24,
            }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600 }}>{projectName || 'Loading...'}</h3>
              <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                {projectDescription}
              </p>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Delivery team: <strong style={{ color: 'var(--text)' }}>{orgName}</strong>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(1)} style={{ ...buttonStyle, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)' }}>
                ← Back
              </button>
              <button onClick={() => setStep(3)} style={buttonStyle}>Continue →</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>Notification Preferences</h2>
            <p style={{ margin: '0 0 24px', color: 'var(--text-secondary)', fontSize: 14 }}>
              {"Choose what you'd like to be notified about."}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              <ToggleRow checked disabled label="Change orders requiring your approval" hint="Required" />
              <ToggleRow checked={notifyMilestones} onChange={setNotifyMilestones} label="Phase completions" />
              <ToggleRow checked={notifyWeekly} onChange={setNotifyWeekly} label="Weekly delivery summary" />
              <ToggleRow checked={notifyDeployments} onChange={setNotifyDeployments} label="Deployment events" />
            </div>

            <div style={{ marginBottom: 24 }}>
              <span style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 8 }}>Channel</span>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                  <input type="radio" checked={channel === 'EMAIL'} onChange={() => setChannel('EMAIL')} />
                  Email
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                  <input type="radio" checked={channel === 'IN_APP'} onChange={() => setChannel('IN_APP')} />
                  In-app only
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(2)} style={{ ...buttonStyle, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)' }}>
                ← Back
              </button>
              <button onClick={handleComplete} disabled={loading} style={{ ...buttonStyle, opacity: loading ? 0.6 : 1 }}>
                {loading ? 'Setting up...' : 'Complete Setup →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ checked, onChange, disabled, label, hint }: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  label: string;
  hint?: string;
}) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px', borderRadius: 8,
      background: checked ? 'rgba(37,99,235,0.06)' : 'transparent',
      cursor: disabled ? 'default' : 'pointer',
      fontSize: 14,
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange?.(e.target.checked)}
        disabled={disabled}
        style={{ width: 18, height: 18 }}
      />
      <span style={{ flex: 1 }}>{label}</span>
      {hint && <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>{hint}</span>}
    </label>
  );
}
